import {
  DefaultApi,
  DeleteTableauAppInfoRequest,
  TableauAppInfo,
  TableauConnectedAppTrustType,
  UpdateTableauAppInfoRequest,
} from "@coeff/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";

import { QueryCacheKey } from "../api";
import {
  Alert,
  AlertExclamation,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Checkbox,
  Form,
  ImportTypeIcon,
  Input,
  LoaderWithPerfTimings,
  Modal,
  OAuthActions,
  OAuthCancelButton,
  OAuthConfiguredAlert,
  OAuthDeleteButton,
  OAuthEditButton,
  OAuthSaveButton,
  SettingsLayout,
  Tooltip,
  Typography,
  WheelIcon,
} from "../components";
import { COLORS } from "../constants";
import { AppState } from "../store";
import { useApiContext, useTrack } from "../utils";

import { Header } from "./Dashboard/components";

const Spacer = styled.div`
  height: 8px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 600px;
`;

const Container = styled.div`
  padding: 25px 30px 48px;
  max-width: 660px;
`;

const StyledHeader = styled(Header)`
  h1 {
    margin: 0 10px;
  }

  .helpGuide {
    display: flex;
    align-items: center;
    margin-left: auto !important;
  }
`;

const StyledAlert = styled(Alert)`
  line-height: 1.8em;
  padding: 12px;

  code {
    white-space: nowrap;
    background-color: ${COLORS.black4};
    padding: 4px 6px;
  }

  .ant-alert-icon {
    margin: 6px;
  }
`;

const FormGroup = styled.div`
  label {
    font-weight: bold;
    margin: 20px 12px 8px 2px;
  }
  margin-bottom: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 40px -15px -10px -15px;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 20px;
`;

interface EditableFormValues {
  app_name: string;
  issuer_uri: string;
  client_secret_id: string;
  client_secret: string;
}

const HelpGuide = () => {
  const track = useTrack();
  return (
    <div className="helpGuide">
      <WheelIcon></WheelIcon>
      <Button type="link" noPadding>
        <a
          href="https://coefficient.notion.site/Tableau-OAuth-via-Okta-1ee0952b59ee803e810dcd9cd6093f9b?pvs=4"
          target="_blank"
          onClick={() => {
            track("help_center_clicked", {
              event_from: "workspace_tableau_oauth_settings_manage",
              import_data_source: "tableau",
            });
          }}
        >
          Help Guide
        </a>
      </Button>
    </div>
  );
};

export const TableauOAuthManageSettings = () => {
  const clientFlags = useSelector((state: AppState) => state.app.clientFlags);

  const track = useTrack();

  const history = useHistory();

  const { clientId } = useParams<{ clientId: string }>();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [verificationError, setVerificationError] = useState<Map<string, string> | null>(null);
  const [formValues, setFormValues] = useState<EditableFormValues>({
    app_name: "",
    issuer_uri: "",
    client_secret_id: "",
    client_secret: "",
  });

  const [_, contextHolder] = message.useMessage();

  const { apiClient: api } = useApiContext();

  const queryClient = useQueryClient();

  const fetchTableauAppInfoList = async (api: DefaultApi) => {
    const response = await api.listTableauAppInfo();
    return response?.data?.apps_list;
  };

  const {
    data: tableauAppInfo,
    isFetching: isFetchingAppInfo,
    error: errorFetchingAppInfo,
  } = useQuery([QueryCacheKey.TABLEAU_APP_INFO, clientId], () => fetchTableauAppInfoList(api), {
    select: (appsList): TableauAppInfo | undefined =>
      appsList?.find(app => app.client_id === clientId),
  });

  // Initialize form values when tableauAppInfo is loaded
  useEffect(() => {
    if (tableauAppInfo) {
      setFormValues({
        app_name: tableauAppInfo.app_name ?? "",
        issuer_uri: tableauAppInfo.issuer_uri ?? "",
        client_secret_id: tableauAppInfo.client_secret_id ?? "",
        client_secret: "",
      });
    }
  }, [tableauAppInfo]);

  const deleteTableauAppInfo = async (
    api: DefaultApi,
    delete_tableau_app_info_request: DeleteTableauAppInfoRequest
  ) => {
    await api.deleteTableauAppInfo({ delete_tableau_app_info_request });
  };

  const updateTableauAppInfo = async (
    api: DefaultApi,
    update_tableau_app_info_request: UpdateTableauAppInfoRequest
  ) => {
    await api.updateTableauAppInfo({ update_tableau_app_info_request });
  };

  const deleteAppInfoMutation = useMutation(
    (appInfo: TableauAppInfo) =>
      deleteTableauAppInfo(api, {
        client_id: appInfo.client_id ?? "",
        domain: appInfo.domain,
      }),
    {
      onSuccess: () => {
        setShowDeleteModal(false);
        history.goBack();
        message.success("Tableau OAuth configuration deleted");
        queryClient.invalidateQueries([QueryCacheKey.TABLEAU_APP_INFO]);
      },
      onError: () => message.error("Something went wrong. Please try again."),
    }
  );

  const updateAppInfoMutation = useMutation(
    (values: EditableFormValues) =>
      updateTableauAppInfo(api, {
        client_id: clientId,
        client_secret: values.client_secret.trim() || undefined,
        domain: tableauAppInfo?.domain,
        app_name: values.app_name.trim(),
        issuer_uri: values.issuer_uri.trim() || undefined,
        client_secret_id: values.client_secret_id.trim() || undefined,
      }),
    {
      onSuccess: () => {
        setIsEditMode(false);
        setVerificationError(null);
        message.success("Saved successfully");
        queryClient.invalidateQueries([QueryCacheKey.TABLEAU_APP_INFO]);
      },
      onError: () => message.error("Something went wrong. Please try again."),
    }
  );

  const verifyConnectionMutation = useMutation(
    async (values: EditableFormValues) => {
      if (
        tableauAppInfo?.tableau_connected_app_trust_type !==
        TableauConnectedAppTrustType.DirectTrust
      ) {
        // OAuth Trust: Validate issuer URI via well-known endpoint
        const wellKnownUrl = `${values.issuer_uri}/.well-known/openid-configuration`;
        const response = await fetch(wellKnownUrl, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Invalid Issuer URL: OpenID configuration unavailable on issuer URL");
        }
        return values;
      }

      // Direct Trust: Verify via API
      const checkResponse = await api.tableauCheckConnection({
        tableau_check_connection_request: {
          credentials: {
            url: tableauAppInfo.instance_url,
            client_id: tableauAppInfo.client_id,
            client_secret: values.client_secret ?? undefined,
            client_secret_id: values.client_secret_id ?? undefined,
            tableau_site_name: tableauAppInfo.site_name,
            tableau_connected_app_trust_type: TableauConnectedAppTrustType.DirectTrust,
            username: clientFlags?.email,
          },
        },
      });
      if (!checkResponse.data.valid) {
        const errorMsg = checkResponse.data.error || "Connection verification failed";
        throw new Error(checkResponse.data.title || errorMsg);
      }
      return values;
    },
    {
      onSuccess: values => {
        updateAppInfoMutation.mutate(values);
      },
      onError: (error: any) => {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Something went wrong. Please try again.";
        setVerificationError(
          () =>
            new Map([
              ["title", error.response?.data?.title || "Connection verification failed"],
              ["message", errorMessage],
            ])
        );
        message.error(errorMessage);
      },
    }
  );

  const handleEditClick = () => {
    setIsEditMode(true);
    setVerificationError(null);
  };

  const handleCancelEdit = () => {
    // Reset form values to original
    if (tableauAppInfo) {
      setFormValues({
        app_name: tableauAppInfo.app_name ?? "",
        issuer_uri: tableauAppInfo.issuer_uri ?? "",
        client_secret_id: tableauAppInfo.client_secret_id ?? "",
        client_secret: "",
      });
    }
    setIsEditMode(false);
    setVerificationError(null);
  };

  const isSaving = useMemo(
    () => verifyConnectionMutation.isLoading || updateAppInfoMutation.isLoading,
    [verifyConnectionMutation.isLoading, updateAppInfoMutation.isLoading]
  );

  const handleInputChange = (field: keyof EditableFormValues, value: string) => {
    setFormValues(prev => ({ ...prev, [field]: value }));
  };

  // Check if client_secret_id has been changed (for Direct Trust)
  const hasSecretIdChange = useMemo((): boolean => {
    return (
      formValues.client_secret_id.trim().length > 0 &&
      formValues.client_secret_id.trim() !== (tableauAppInfo?.client_secret_id ?? "")
    );
  }, [formValues.client_secret_id, tableauAppInfo?.client_secret_id]);

  // Check if client_secret has been provided
  const hasSecretChange = useMemo((): boolean => {
    return formValues.client_secret.trim().length > 0;
  }, [formValues.client_secret]);

  // Check if issuer_uri has been changed (for OAuth Trust)
  const hasIssuerChange = useMemo((): boolean => {
    return (
      formValues.issuer_uri.trim().length > 0 &&
      formValues.issuer_uri.trim() !== (tableauAppInfo?.issuer_uri ?? "")
    );
  }, [formValues.issuer_uri, tableauAppInfo?.issuer_uri]);

  // Check if any credential fields have been changed
  const hasCredentialChanges = useMemo((): boolean => {
    return hasSecretChange || hasIssuerChange || hasSecretIdChange;
  }, [hasSecretChange, hasIssuerChange, hasSecretIdChange]);

  // Check if form is valid
  const isFormValid = useMemo((): boolean => {
    // app_name is always required
    if (!formValues.app_name.trim()) {
      return false;
    }

    // For OAuth Trust: issuer_uri is required (cannot be cleared)
    if (
      tableauAppInfo?.tableau_connected_app_trust_type !==
        TableauConnectedAppTrustType.DirectTrust &&
      !formValues.issuer_uri.trim()
    ) {
      return false;
    }

    // For Direct Trust: client_secret_id is required (cannot be cleared)
    if (
      tableauAppInfo?.tableau_connected_app_trust_type ===
        TableauConnectedAppTrustType.DirectTrust &&
      !formValues.client_secret_id.trim()
    ) {
      return false;
    }

    // For Direct Trust: if client_secret_id is changed, client_secret is required
    if (
      tableauAppInfo?.tableau_connected_app_trust_type ===
        TableauConnectedAppTrustType.DirectTrust &&
      hasSecretIdChange &&
      !hasSecretChange
    ) {
      return false;
    }

    return true;
  }, [
    formValues.app_name,
    formValues.issuer_uri,
    formValues.client_secret_id,
    tableauAppInfo?.tableau_connected_app_trust_type,
    hasSecretIdChange,
    hasSecretChange,
  ]);

  // Check if client_secret is required but missing (for Direct Trust when client_secret_id changes)
  const isClientSecretRequired = useMemo((): boolean => {
    return (
      tableauAppInfo?.tableau_connected_app_trust_type ===
        TableauConnectedAppTrustType.DirectTrust &&
      hasSecretIdChange &&
      !hasSecretChange
    );
  }, [tableauAppInfo?.tableau_connected_app_trust_type, hasSecretIdChange, hasSecretChange]);

  const handleSave = () => {
    setVerificationError(null);
    // Skip verification if only app_name is being changed (no credential changes)
    if (hasCredentialChanges) {
      verifyConnectionMutation.mutate(formValues);
    } else {
      // Directly update without verification
      updateAppInfoMutation.mutate(formValues);
    }
  };

  const renderContent = (children: React.ReactNode) => {
    return (
      <Container>
        <Breadcrumb>
          <BreadcrumbItem>
            <Link to={{ pathname: `/dashboard/admin` }}>Admin</Link>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <Link to={{ pathname: `/dashboard/admin/settings` }}>Settings</Link>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <Link to={{ pathname: `/dashboard/settings/oauth-settings` }}>OAuth Settings</Link>
          </BreadcrumbItem>
        </Breadcrumb>
        <StyledHeader>
          <ImportTypeIcon type="tableau" />
          <h1>Tableau OAuth</h1>
          <HelpGuide />
        </StyledHeader>
        {contextHolder}
        {children}
      </Container>
    );
  };

  if (clientFlags?.is_gmail_like_domain) {
    return (
      <SettingsLayout dataSourceType="tableau" title="Tableau OAuth Settings">
        <StyledAlert
          type="error"
          showIcon
          icon={<AlertExclamation type="error" />}
          message="Tableau OAuth is not supported for your domain"
          description={`Tableau OAuth is not supported for ${
            clientFlags.email.split("@")[1]
          }. Please use a different account to configure Tableau OAuth.`}
        />
      </SettingsLayout>
    );
  }

  if (
    clientFlags?.admin_emails &&
    clientFlags?.admin_emails.length > 0 &&
    !clientFlags?.admin_emails?.includes(clientFlags.email)
  ) {
    return (
      <SettingsLayout dataSourceType="tableau" title="Tableau OAuth Settings">
        <StyledAlert
          type="error"
          showIcon
          icon={<AlertExclamation type="error" />}
          message="You need to be an admin to access this page"
          description="Please contact your domain admin for configuring or managing Tableau accounts."
        />
      </SettingsLayout>
    );
  }

  if (deleteAppInfoMutation.isLoading || updateAppInfoMutation.isLoading || isFetchingAppInfo) {
    return renderContent(<LoaderWithPerfTimings name="TableauOAuthSettings" />);
  }

  if (errorFetchingAppInfo) {
    return renderContent(
      <StyledAlert
        type="error"
        showIcon
        icon={<AlertExclamation type="error" />}
        message="Error loading Tableau OAuth Settings"
        description="Please try refreshing this page, or contact support@coefficient.io"
      />
    );
  }

  if (!tableauAppInfo) {
    return renderContent(
      <StyledAlert
        type="error"
        showIcon
        icon={<AlertExclamation type="error" />}
        message="Could not find Tableau OAuth Settings for this account"
        description="It's possible that the settings have been deleted."
      />
    );
  }

  const dataImportCount = tableauAppInfo.data_import_count ?? 0;
  const dataSourceCount = tableauAppInfo.data_source_count ?? 0;
  const quantify = (count: number, singular: string, multiple: (n: number) => string): string =>
    count === 1 ? singular : multiple(count);
  const qImports = quantify(dataImportCount, "1 import", n => `${n} imports`);
  const qConnections = quantify(dataSourceCount, "1 connection", n => `${n} connections`);
  const editContentWarningDescription = () => {
    if (
      tableauAppInfo.tableau_connected_app_trust_type !== TableauConnectedAppTrustType.DirectTrust
    ) {
      return "Changing the Issuer URI or Client Secret will stop refreshes for existing imports. To resume refreshes, reauthorize the affected Tableau connection in Coefficient.";
    } else {
      return "Changing the Client Secret ID or Client Secret will stop refreshes for existing imports. To resume refreshes, reauthorize the affected Tableau connection in Coefficient.";
    }
  };
  const editContentWarningMessage = () => {
    if (
      tableauAppInfo.tableau_connected_app_trust_type !== TableauConnectedAppTrustType.DirectTrust
    ) {
      return "Editing the Issuer URI or Client Secret requires reauthorization.";
    } else {
      return "Editing the Client Secret ID or Client Secret requires reauthorization.";
    }
  };

  return renderContent(
    <Content>
      {verificationError && (
        <StyledAlert
          type="error"
          showIcon
          icon={<AlertExclamation type="error" />}
          message={verificationError?.get("title") || "Connection verification failed"}
          description={verificationError?.get("message") || "Verify your app configuration."}
          style={{ marginBottom: 16 }}
        />
      )}
      {isEditMode ? (
        <StyledAlert
          type="warning"
          showIcon
          icon={<AlertExclamation type="warning" />}
          message={editContentWarningMessage()}
          description={editContentWarningDescription()}
        />
      ) : (
        <OAuthConfiguredAlert dataSourceType="tableau" />
      )}
      <Form style={{ marginTop: "30px" }}>
        <FormGroup>
          <label>Connected App Trust Type</label>
          <Input
            disabled
            value={
              tableauAppInfo.tableau_connected_app_trust_type ===
              TableauConnectedAppTrustType.DirectTrust
                ? "Direct Trust"
                : "OAuth 2.0 Trust"
            }
          />
        </FormGroup>
        <FormGroup>
          <Tooltip
            title="App Name is required"
            open={!formValues.app_name.trim() ? undefined : false}
          >
            <label>Name</label>
            <Input
              disabled={!isEditMode}
              status={!formValues.app_name.trim() ? "error" : undefined}
              value={isEditMode ? formValues.app_name : tableauAppInfo.app_name}
              onChange={e => handleInputChange("app_name", e.target.value)}
            />
          </Tooltip>
        </FormGroup>
        {tableauAppInfo.tableau_connected_app_trust_type !==
          TableauConnectedAppTrustType.DirectTrust && (
          <FormGroup>
            <label>Issuer URI</label>
            <Input
              disabled={!isEditMode}
              value={isEditMode ? formValues.issuer_uri : tableauAppInfo.issuer_uri}
              onChange={e => handleInputChange("issuer_uri", e.target.value)}
            />
          </FormGroup>
        )}
        <FormGroup>
          <label>Client ID</label>
          <Input disabled value={tableauAppInfo.client_id} />
        </FormGroup>
        {tableauAppInfo.tableau_connected_app_trust_type ===
          TableauConnectedAppTrustType.DirectTrust && (
          <FormGroup>
            <label>Client Secret ID</label>
            <Input
              disabled={!isEditMode}
              value={isEditMode ? formValues.client_secret_id : tableauAppInfo.client_secret_id}
              onChange={e => handleInputChange("client_secret_id", e.target.value)}
            />
          </FormGroup>
        )}
        <FormGroup>
          <label>Client Secret</label>
          <Tooltip
            title="Client Secret is required when changing Client Secret ID"
            open={isEditMode && isClientSecretRequired ? undefined : false}
          >
            <Input
              disabled={!isEditMode}
              type="password"
              status={isEditMode && isClientSecretRequired ? "error" : undefined}
              value={isEditMode ? formValues.client_secret : tableauAppInfo.client_secret}
              onChange={e => handleInputChange("client_secret", e.target.value)}
              placeholder={"******************************"}
            />
          </Tooltip>
        </FormGroup>
        <FormGroup>
          <label>Instance URL</label>
          <Input disabled value={tableauAppInfo.instance_url} />
        </FormGroup>
        <FormGroup>
          <label>Site Name</label>
          <Input disabled value={tableauAppInfo.site_name} />
        </FormGroup>
        {isEditMode ? (
          <OAuthActions>
            <OAuthSaveButton
              htmlType="button"
              onClick={handleSave}
              loading={isSaving}
              disabled={!isFormValid || isSaving}
            >
              {verifyConnectionMutation.isLoading ? "Verifying..." : "Save"}
            </OAuthSaveButton>
            <OAuthCancelButton htmlType="button" onClick={handleCancelEdit} disabled={isSaving} />
          </OAuthActions>
        ) : (
          <OAuthActions>
            <OAuthEditButton htmlType="button" onClick={handleEditClick} />
            <OAuthDeleteButton
              htmlType="button"
              onClick={() => {
                setDeleteConfirmed(false);
                setShowDeleteModal(true);
              }}
            />
          </OAuthActions>
        )}
      </Form>
      <Modal open={showDeleteModal} onCancel={() => setShowDeleteModal(false)}>
        <Typography fontWeight="bold" variant="h6" gutterBottom>
          Please Confirm
        </Typography>
        <Typography color="textSecondary">
          Are you sure you want to delete this tableau configuration?
        </Typography>

        {(dataImportCount > 0 || dataSourceCount > 0) && (
          <StyledAlert
            showIcon
            banner
            icon={<AlertExclamation type="warning" />}
            type="warning"
            message="Warning!"
            description={`${qConnections} and ${qImports} will stop refreshing if this configuration is deleted.`}
          />
        )}
        <StyledCheckbox
          onChange={() => setDeleteConfirmed(prev => !prev)}
          checked={deleteConfirmed}
        >
          Got it, please proceed
        </StyledCheckbox>
        <Footer>
          <Button
            type="text"
            disabled={deleteAppInfoMutation.isLoading}
            onClick={() => {
              setShowDeleteModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            type="text"
            danger
            disabled={!deleteConfirmed || deleteAppInfoMutation.isLoading}
            onClick={() => {
              if (tableauAppInfo.client_id && deleteConfirmed) {
                deleteAppInfoMutation.mutate(tableauAppInfo);
              }
            }}
          >
            Delete
          </Button>
        </Footer>
      </Modal>
    </Content>
  );
};
